import { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, Trash2, Upload, X, FileText, Info, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import Layout, { PageHeader } from "@/components/Layout";
import { Card } from "@/components/ui/Cards";
import { Button } from "@/components/ui/Buttons";
import { Input, Textarea } from "@/components/ui/Inputs";
import NextStepFooter from "@/components/NextStepFooter";
import AiBrainPanel from "@/components/AiBrainPanel";
import { ConfirmDeleteDialog } from "@/components/ConfirmDeleteDialog";
import { useProject } from "@/hooks/useProjects";
import { useOrgContext, useUpdateOrgContext, orgContextKey } from "@/hooks/useOrgContext";
import { useFileUploadList } from "@/hooks/useFileUploadList";
import { api, type ToneOfVoice, type ProjectFile, type FileOutcome } from "@/lib/api";
import { cn } from "@/lib/utils";

export default function AboutUs() {
  const params = useParams<{ id?: string }>();
  const projectId = params.id;

  const { data: project } = useProject(projectId ?? null);
  const { data: orgContextData, isLoading } = useOrgContext();
  const updateOrgContext = useUpdateOrgContext();
  const qc = useQueryClient();

  const [isDirty, setIsDirty] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const [autosaveError, setAutosaveError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    agencyDescription: "",
    positioning: "",
    toneOfVoice: null as ToneOfVoice | null,
    focusAreas: [] as string[],
    differentiators: "",
    additionalNotes: "",
    brandGuideFileId: null as string | null,
  });

  // Hydrate the form once on initial load. Never re-hydrate after that — it
  // would clobber unsaved keystrokes if a refetch landed mid-edit.
  useEffect(() => {
    if (orgContextData?.context && !hydrated) {
      const ctx = orgContextData.context;
      setFormData({
        agencyDescription: ctx.agencyDescription,
        positioning: ctx.positioning,
        toneOfVoice: ctx.toneOfVoice,
        focusAreas: ctx.focusAreas,
        differentiators: ctx.differentiators,
        additionalNotes: ctx.additionalNotes,
        brandGuideFileId: ctx.brandGuideFileId,
      });
      setIsDirty(false);
      setHydrated(true);
    }
  }, [orgContextData, hydrated]);

  // Autosave: 1.2s debounce after the last change. Fires only after the
  // initial hydrate so we don't save the empty defaults right after mount.
  useEffect(() => {
    if (!hydrated || !isDirty) return;
    const t = setTimeout(async () => {
      try {
        await updateOrgContext.mutateAsync(formData);
        setIsDirty(false);
        setLastSavedAt(Date.now());
        setAutosaveError(null);
      } catch (err) {
        setAutosaveError((err as Error).message ?? "Couldn't save.");
      }
    }, 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, isDirty, hydrated]);

  const toneOptions: ToneOfVoice[] = [
    "professional",
    "conversational",
    "bold",
    "warm",
    "technical",
    "playful",
  ];

  const toneLabels: Record<ToneOfVoice, string> = {
    professional: "Professional",
    conversational: "Conversational",
    bold: "Bold",
    warm: "Warm",
    technical: "Technical",
    playful: "Playful",
  };

  const handleAddFocusArea = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const input = e.currentTarget;
      const value = input.value.trim();
      if (value && !formData.focusAreas.includes(value)) {
        setFormData((prev) => ({
          ...prev,
          focusAreas: [...prev.focusAreas, value],
        }));
        input.value = "";
        setIsDirty(true);
      }
    }
  };

  const handleRemoveFocusArea = (area: string) => {
    setFormData((prev) => ({
      ...prev,
      focusAreas: prev.focusAreas.filter((a) => a !== area),
    }));
    setIsDirty(true);
  };

  // (Manual save handler removed — autosave handles it. Kept for fallback if
  // we want a "Save now" button later.)

  // Multi-file upload for agency reference docs (kind="agency_doc",
  // projectId=null so they live at org level, shared across all projects).
  const {
    items: uploadItems,
    addFiles,
    removeItem: removeUploadItem,
    reset: resetUploads,
  } = useFileUploadList({ kind: "agency_doc" });

  // Separate upload list for past RFP submissions (kind="past_rfp").
  const {
    items: pastRfpUploadItems,
    addFiles: addPastRfpFiles,
    removeItem: removePastRfpUploadItem,
    reset: resetPastRfpUploads,
  } = useFileUploadList({ kind: "past_rfp" });

  const [pendingDeleteFile, setPendingDeleteFile] = useState<ProjectFile | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // After uploads finish (either bucket), refetch org context so new files
  // appear. Use React Query invalidation instead of a full page reload — that
  // preserves any unsaved form keystrokes the user has in flight.
  useEffect(() => {
    const allItems = [...uploadItems, ...pastRfpUploadItems];
    const allDone =
      allItems.length > 0 && allItems.every((i) => i.status === "done" || i.status === "error");
    const anyDone = allItems.some((i) => i.status === "done");
    if (!allDone || !anyDone) return;
    const t = setTimeout(() => {
      qc.invalidateQueries({ queryKey: orgContextKey });
    }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    uploadItems.map((i) => `${i.tempId}:${i.status}`).join("|"),
    pastRfpUploadItems.map((i) => `${i.tempId}:${i.status}`).join("|"),
  ]);

  const handleConfirmDeleteFile = async () => {
    if (!pendingDeleteFile) return;
    setIsDeleting(true);
    try {
      await api.deleteOrgFile(pendingDeleteFile.id);
      toast.success("File removed.");
      setPendingDeleteFile(null);
      qc.invalidateQueries({ queryKey: orgContextKey });
    } catch (err) {
      toast.error((err as Error).message ?? "Couldn't remove the file.");
    } finally {
      setIsDeleting(false);
    }
  };

  const orgDocs: ProjectFile[] = orgContextData?.orgDocs ?? [];
  const pastRfpFiles: ProjectFile[] = orgContextData?.pastRfpFiles ?? [];

  if (isLoading) {
    return (
      <Layout projectId={projectId}>
        <PageHeader title="About Us" subtitle="Loading…" />
        <div className="flex items-center justify-center py-24">
          <Loader2 size={20} className="animate-spin text-orange-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout projectTitle={project?.title} projectId={projectId}>
      <PageHeader title="About Us" subtitle="Your agency's positioning, voice, and past work" />

      <div className="p-8 pb-40 max-w-4xl space-y-6">
        {/* Info banner */}
        <Card className="border-orange-400/20 bg-orange-400/5">
          <div className="flex items-start gap-3">
            <div className="text-orange-400 text-lg">ℹ</div>
            <div>
              <p className="text-sm font-medium text-white">
                This information is shared across all your projects
              </p>
              <p className="text-xs mt-1" style={{ color: "#9CA3AF" }}>
                Set it once and it powers every AI draft. The AI reads your agency's voice,
                positioning, and past work to write proposals that sound like you and reference your
                relevant wins.
              </p>
            </div>
          </div>
        </Card>

        {/* AI Brain Panel */}
        {projectId && project && (
          <AiBrainPanel
            project={project}
            projectId={projectId}
            orgContext={orgContextData?.context}
          />
        )}

        {/* Form */}
        <Card>
          <div className="space-y-6">
            {/* Agency Description */}
            <div>
              <FieldLabel
                label="Agency Description"
                hint="Who you are, what you do, who you serve. Pull straight from your About page or capability deck — 1-2 paragraphs is plenty. The AI uses this for every proposal's intro and 'about us' sections."
              />
              <Textarea
                value={formData.agencyDescription}
                onChange={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    agencyDescription: e.target.value,
                  }));
                  setIsDirty(true);
                }}
                placeholder="Who you are, what you do, who you do it for…"
                rows={4}
                className="w-full"
              />
            </div>

            {/* Positioning */}
            <div>
              <FieldLabel
                label="Positioning"
                hint="Your one-sentence pitch — what you stand for and how you're different. Often the headline on your homepage or the first slide of your sales deck."
              />
              <Textarea
                value={formData.positioning}
                onChange={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    positioning: e.target.value,
                  }));
                  setIsDirty(true);
                }}
                placeholder="The one-line pitch that sums you up. e.g. 'The Caribbean's only data-driven brand consultancy.'"
                rows={3}
                className="w-full"
              />
            </div>

            {/* Tone of Voice */}
            <div>
              <FieldLabel
                label="Tone of Voice"
                hint="How you sound when you talk to clients. If you have a brand voice guide or a style document, pick the closest match. The AI uses this to set the language register in drafts."
              />
              <div className="flex flex-wrap gap-2">
                {toneOptions.map((tone) => (
                  <button
                    key={tone}
                    onClick={() => {
                      setFormData((prev) => ({
                        ...prev,
                        toneOfVoice: prev.toneOfVoice === tone ? null : tone,
                      }));
                      setIsDirty(true);
                    }}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      formData.toneOfVoice === tone
                        ? "bg-orange-400 text-black"
                        : "bg-gray-700/50 text-gray-300 hover:bg-gray-600/50"
                    }`}
                  >
                    {toneLabels[tone]}
                  </button>
                ))}
              </div>
            </div>

            {/* Focus Areas */}
            <div>
              <FieldLabel
                label="Focus Areas"
                hint="The categories of work you specialise in. Examples: brand strategy, digital marketing, creative production, research, public relations, content, video. Look at your services page or capability deck for these. Press Enter after each one to add it as a chip."
              />
              <div className="space-y-2">
                {formData.focusAreas.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {formData.focusAreas.map((area) => (
                      <div
                        key={area}
                        className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-400/20 text-orange-300 text-sm"
                      >
                        {area}
                        <button
                          onClick={() => handleRemoveFocusArea(area)}
                          className="hover:text-orange-200 transition-colors"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <Input
                  placeholder="Type a focus area and press Enter to add"
                  onKeyDown={handleAddFocusArea}
                  className="w-full"
                />
              </div>
            </div>

            {/* Differentiators */}
            <div>
              <FieldLabel
                label="Differentiators"
                hint="What makes you hard to replace. Specific certifications, awards, exclusive capabilities, sector expertise, named team experts, proprietary methods. Look in your case studies and pitch decks — the bits you brag about are usually here."
              />
              <Textarea
                value={formData.differentiators}
                onChange={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    differentiators: e.target.value,
                  }));
                  setIsDirty(true);
                }}
                placeholder="What makes your work hard to copy. Specific capabilities, certifications, sector wins…"
                rows={3}
                className="w-full"
              />
            </div>

            {/* Additional Notes */}
            <div>
              <FieldLabel
                label="Additional Notes"
                hint="Anything else worth knowing — internal preferences, language to avoid, recurring themes the AI should weave in, or context the other fields don't capture."
              />
              <Textarea
                value={formData.additionalNotes}
                onChange={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    additionalNotes: e.target.value,
                  }));
                  setIsDirty(true);
                }}
                placeholder="Anything else the AI should know."
                rows={3}
                className="w-full"
              />
            </div>

            {/* Autosave status — replaces the manual Save button. */}
            <AutosaveStatus
              dirty={isDirty}
              saving={updateOrgContext.isPending}
              lastSavedAt={lastSavedAt}
              error={autosaveError}
            />
          </div>
        </Card>

        {/* Past RFP Submissions — upload + per-file metadata (outcome, paired RFP, notes). */}
        <Card>
          <div className="space-y-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-sm font-semibold text-white">Past RFP Submissions</h3>
              </div>
              <p className="text-xs" style={{ color: "#6B7280" }}>
                Upload past responses you've sent. For each, mark whether you won, lost, or just
                submitted, optionally pair it with the original RFP scope it was written against,
                and add a short note about why it landed the way it did. The AI uses these as the
                strongest examples of your house style.
              </p>
            </div>

            <PastRfpUploadZone onAddFiles={addPastRfpFiles} />

            {/* In-flight uploads */}
            {pastRfpUploadItems.length > 0 && (
              <div className="space-y-2">
                {pastRfpUploadItems.map((item) => (
                  <div
                    key={item.tempId}
                    className="flex items-center gap-3 p-3 rounded-lg border"
                    style={{
                      backgroundColor: "rgba(255,255,255,0.02)",
                      borderColor: "rgba(229,231,235,0.06)",
                    }}
                  >
                    <FileText size={14} className="text-orange-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">{item.filename}</p>
                      <p className="text-xs" style={{ color: "#6B7280" }}>
                        {(item.byteSize / 1024 / 1024).toFixed(1)} MB ·{" "}
                        {item.status === "uploading"
                          ? `Uploading ${item.progressPct}%`
                          : item.status === "done"
                            ? "Done"
                            : item.status === "error"
                              ? `Error: ${item.error ?? "upload failed"}`
                              : "Waiting"}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => removePastRfpUploadItem(item.tempId)}
                      className="p-1.5 rounded text-gray-400 hover:text-red-400 hover:bg-white/5 flex-shrink-0"
                      title="Remove from list"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => resetPastRfpUploads()}
                  className="text-xs text-gray-400 hover:text-white"
                >
                  Clear list
                </button>
              </div>
            )}

            {/* Saved past-RFP files with editable metadata */}
            {pastRfpFiles.length > 0 ? (
              <div className="space-y-3">
                {pastRfpFiles.map((file) => (
                  <PastRfpRow
                    key={file.id}
                    file={file}
                    pairCandidates={orgDocs}
                    onDelete={() => setPendingDeleteFile(file)}
                  />
                ))}
              </div>
            ) : pastRfpUploadItems.length === 0 ? (
              <p className="text-xs text-center py-4" style={{ color: "#6B7280" }}>
                No past RFPs uploaded yet.
              </p>
            ) : null}

            {/* Auto-detected past projects from this app */}
            {orgContextData?.pastProjects && orgContextData.pastProjects.length > 0 && (
              <div className="mt-4 pt-4 border-t" style={{ borderColor: "rgba(229,231,235,0.06)" }}>
                <p className="text-xs font-semibold mb-2" style={{ color: "#9CA3AF" }}>
                  Also tracked from this app
                </p>
                <div className="space-y-2">
                  {orgContextData.pastProjects.map((p) => (
                    <div
                      key={p.id}
                      className="flex items-center justify-between p-2 rounded-lg"
                      style={{ backgroundColor: "rgba(255,255,255,0.02)" }}
                    >
                      <div className="flex-1">
                        <p className="text-sm font-medium text-white">{p.title}</p>
                        <p className="text-xs text-gray-400">
                          {p.requesterName} • {new Date(p.submittedAt).toLocaleDateString()}
                        </p>
                      </div>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          p.status === "won"
                            ? "bg-green-400/20 text-green-300"
                            : p.status === "lost"
                              ? "bg-red-400/20 text-red-300"
                              : "bg-gray-600/30 text-gray-300"
                        }`}
                      >
                        {p.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Reference Documents — multi-file upload for agency materials */}
        <Card>
          <div className="space-y-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-sm font-semibold text-white">Reference documents</h3>
                <SuggestedFilesTooltip suggestions={ABOUT_US_SUGGESTIONS} />
              </div>
              <p className="text-xs" style={{ color: "#6B7280" }}>
                Upload anything that helps the AI write proposals that sound like you. These are
                stored at the agency level and used across every project.
              </p>
            </div>

            <DocsUploadZone onAddFiles={addFiles} />

            {/* In-flight uploads */}
            {uploadItems.length > 0 && (
              <div className="space-y-2">
                {uploadItems.map((item) => (
                  <div
                    key={item.tempId}
                    className="flex items-center gap-3 p-3 rounded-lg border"
                    style={{
                      backgroundColor: "rgba(255,255,255,0.02)",
                      borderColor: "rgba(229,231,235,0.06)",
                    }}
                  >
                    <FileText size={14} className="text-orange-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">{item.filename}</p>
                      <p className="text-xs" style={{ color: "#6B7280" }}>
                        {(item.byteSize / 1024 / 1024).toFixed(1)} MB ·{" "}
                        {item.status === "uploading"
                          ? `Uploading ${item.progressPct}%`
                          : item.status === "done"
                            ? "Done"
                            : item.status === "error"
                              ? `Error: ${item.error ?? "upload failed"}`
                              : "Waiting"}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeUploadItem(item.tempId)}
                      className="p-1.5 rounded text-gray-400 hover:text-red-400 hover:bg-white/5 flex-shrink-0"
                      title="Remove from list"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => resetUploads()}
                  className="text-xs text-gray-400 hover:text-white"
                >
                  Clear list
                </button>
              </div>
            )}

            {/* Saved org-level docs */}
            {orgDocs.length > 0 ? (
              <div className="space-y-2">
                {orgDocs.map((file) => (
                  <div
                    key={file.id}
                    className="flex items-center gap-3 p-3 rounded-lg border"
                    style={{
                      backgroundColor: "rgba(255,255,255,0.02)",
                      borderColor: "rgba(229,231,235,0.06)",
                    }}
                  >
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: "rgba(249,115,22,0.1)" }}
                    >
                      <FileText size={14} style={{ color: "#F97316" }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">{file.filename}</p>
                      <p className="text-xs" style={{ color: "#6B7280" }}>
                        {(file.byteSize / 1024 / 1024).toFixed(1)} MB
                      </p>
                    </div>
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium flex-shrink-0"
                      style={{
                        backgroundColor: "rgba(34,197,94,0.12)",
                        color: "#22c55e",
                      }}
                    >
                      <CheckCircle2 size={10} />
                      Saved
                    </span>
                    <button
                      type="button"
                      onClick={() => setPendingDeleteFile(file)}
                      className="p-1.5 rounded text-gray-400 hover:text-red-400 hover:bg-white/5 flex-shrink-0"
                      title="Remove file"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            ) : uploadItems.length === 0 ? (
              <p className="text-xs text-center py-6" style={{ color: "#6B7280" }}>
                No documents yet. Click the info icon for ideas of what helps.
              </p>
            ) : null}
          </div>
        </Card>

        {/* Past RFP Submissions section is now rendered ABOVE Reference Documents. See below. */}

        <NextStepFooter currentStep="about-us" projectId={projectId!} />
      </div>

      <ConfirmDeleteDialog
        open={pendingDeleteFile !== null}
        onOpenChange={(o) => {
          if (!o) setPendingDeleteFile(null);
        }}
        title="Remove this document?"
        description={
          pendingDeleteFile
            ? `"${pendingDeleteFile.filename}" will be deleted from your agency reference docs. The AI will no longer use it.`
            : ""
        }
        requiredPhrase="remove"
        confirmLabel="Remove"
        onConfirm={handleConfirmDeleteFile}
        busy={isDeleting}
      />
    </Layout>
  );
}

// ---------------------------------------------------------------------------
// FieldLabel — label + small ⓘ icon with hover/click hint explaining the field.
// ---------------------------------------------------------------------------

function FieldLabel({ label, hint }: { label: string; hint: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex items-center gap-2 mb-2">
      <label className="block text-sm font-medium text-white">{label}</label>
      <div className="relative inline-flex">
        <button
          type="button"
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onClick={() => setOpen((v) => !v)}
          className="text-gray-400 hover:text-orange-400"
          aria-label={`About ${label}`}
          title={`About ${label}`}
        >
          <Info size={13} />
        </button>
        {open && (
          <div
            className="absolute left-6 top-0 z-50 w-80 rounded-lg p-3 shadow-lg border"
            style={{
              backgroundColor: "#1F2933",
              borderColor: "rgba(229,231,235,0.1)",
            }}
          >
            <p
              className="text-[10px] uppercase font-semibold mb-1.5"
              style={{ color: "#F97316", letterSpacing: "0.05em" }}
            >
              About this field
            </p>
            <p className="text-xs leading-snug" style={{ color: "#D1D5DB" }}>
              {hint}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AutosaveStatus — small inline indicator showing the form's save state.
// ---------------------------------------------------------------------------

function AutosaveStatus({
  dirty,
  saving,
  lastSavedAt,
  error,
}: {
  dirty: boolean;
  saving: boolean;
  lastSavedAt: number | null;
  error: string | null;
}) {
  // Re-render every 30s so "saved 2 min ago" stays fresh.
  const [, force] = useState(0);
  useEffect(() => {
    const t = setInterval(() => force((v) => v + 1), 30_000);
    return () => clearInterval(t);
  }, []);

  let text: string;
  let color: string;
  if (error) {
    text = `Save failed — ${error}`;
    color = "#ef4444";
  } else if (saving) {
    text = "Saving…";
    color = "#9CA3AF";
  } else if (dirty) {
    text = "Unsaved changes";
    color = "#EAB308";
  } else if (lastSavedAt) {
    text = `Saved ${formatRelative(lastSavedAt)}`;
    color = "#22c55e";
  } else {
    text = "Changes save automatically.";
    color = "#6B7280";
  }

  return (
    <div className="flex items-center justify-end gap-2 text-xs" style={{ color }}>
      {saving && <Loader2 size={12} className="animate-spin" />}
      {!saving && lastSavedAt && !dirty && !error && <CheckCircle2 size={12} />}
      <span>{text}</span>
    </div>
  );
}

function formatRelative(ts: number): string {
  const sec = Math.floor((Date.now() - ts) / 1000);
  if (sec < 5) return "just now";
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

// ---------------------------------------------------------------------------
// Suggestions for what's worth uploading — shown in a hover tooltip.
// ---------------------------------------------------------------------------

const ABOUT_US_SUGGESTIONS = [
  "Brand guide / style guide",
  "Capability deck or service overview",
  "Case studies & client wins",
  "Team bios and CVs",
  "Pricing / rate cards",
  "Press, awards, testimonials",
  "Pre-existing client materials (if you've worked with this client before)",
  "Past proposals belong in the Past RFP Submissions section above",
];

function SuggestedFilesTooltip({ suggestions }: { suggestions: string[] }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative inline-flex">
      <button
        type="button"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={() => setOpen((v) => !v)}
        className="text-gray-400 hover:text-orange-400"
        aria-label="What kind of files help?"
        title="What kind of files help the AI?"
      >
        <Info size={14} />
      </button>
      {open && (
        <div
          className="absolute left-6 top-0 z-50 w-72 rounded-lg p-3 shadow-lg border"
          style={{
            backgroundColor: "#1F2933",
            borderColor: "rgba(229,231,235,0.1)",
          }}
        >
          <p
            className="text-[10px] uppercase font-semibold mb-2"
            style={{ color: "#F97316", letterSpacing: "0.05em" }}
          >
            Files that help the AI
          </p>
          <ul className="space-y-1">
            {suggestions.map((s, i) => (
              <li key={i} className="text-xs leading-snug" style={{ color: "#D1D5DB" }}>
                — {s}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PastRfpUploadZone — same as DocsUploadZone but with copy specific to past
// RFP responses.
// ---------------------------------------------------------------------------

function PastRfpUploadZone({ onAddFiles }: { onAddFiles: (files: File[]) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;
      onAddFiles(Array.from(files));
    },
    [onAddFiles],
  );

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        handleFiles(e.dataTransfer.files);
      }}
      onClick={() => inputRef.current?.click()}
      className={cn(
        "rounded-xl border-2 border-dashed px-6 py-7 flex flex-col items-center justify-center gap-2 cursor-pointer transition-all",
      )}
      style={{
        borderColor: dragOver ? "rgba(249,115,22,0.5)" : "rgba(229,231,235,0.15)",
        backgroundColor: dragOver ? "rgba(249,115,22,0.05)" : "transparent",
      }}
    >
      <input
        ref={inputRef}
        type="file"
        multiple
        accept="application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/png,image/jpeg"
        onChange={(e) => {
          handleFiles(e.target.files);
          if (inputRef.current) inputRef.current.value = "";
        }}
        className="hidden"
      />
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center"
        style={{ backgroundColor: "rgba(249,115,22,0.1)" }}
      >
        <Upload size={18} style={{ color: "#F97316" }} />
      </div>
      <p className="text-sm text-white font-medium">
        Drop past RFP responses here, or click to browse
      </p>
      <p className="text-xs" style={{ color: "#6B7280" }}>
        Multiple files OK · max 10 MB each
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PastRfpRow — saved past-RFP file with editable metadata (outcome, paired
// RFP scope file, agency notes).
// ---------------------------------------------------------------------------

const OUTCOME_LABELS: Record<FileOutcome, string> = {
  won: "Won",
  lost: "Lost",
  submitted: "Submitted",
  draft: "Draft",
};

function PastRfpRow({
  file,
  pairCandidates,
  onDelete,
}: {
  file: ProjectFile;
  pairCandidates: ProjectFile[];
  onDelete: () => void;
}) {
  const [outcome, setOutcome] = useState<FileOutcome | null>(file.outcome ?? null);
  const [pairedFileId, setPairedFileId] = useState<string | null>(file.pairedFileId ?? null);
  const [notes, setNotes] = useState(file.agencyNotes ?? "");
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const isDirty =
    outcome !== (file.outcome ?? null) ||
    pairedFileId !== (file.pairedFileId ?? null) ||
    notes !== (file.agencyNotes ?? "");

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateFileMetadata(file.id, {
        outcome,
        pairedFileId,
        agencyNotes: notes,
      });
      toast.success("Saved.");
      setSavedAt(Date.now());
    } catch (err) {
      toast.error((err as Error).message ?? "Couldn't save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="rounded-lg border p-4 space-y-3"
      style={{
        backgroundColor: "rgba(255,255,255,0.02)",
        borderColor: "rgba(229,231,235,0.06)",
      }}
    >
      {/* Top row: file icon + name + saved badge + delete */}
      <div className="flex items-center gap-3">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: "rgba(249,115,22,0.1)" }}
        >
          <FileText size={14} style={{ color: "#F97316" }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-white truncate">{file.filename}</p>
          <p className="text-xs" style={{ color: "#6B7280" }}>
            {(file.byteSize / 1024 / 1024).toFixed(1)} MB
          </p>
        </div>
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium flex-shrink-0"
          style={{
            backgroundColor: "rgba(34,197,94,0.12)",
            color: "#22c55e",
          }}
        >
          <CheckCircle2 size={10} />
          Saved
        </span>
        <button
          type="button"
          onClick={onDelete}
          className="p-1.5 rounded text-gray-400 hover:text-red-400 hover:bg-white/5 flex-shrink-0"
          title="Remove file"
        >
          <Trash2 size={14} />
        </button>
      </div>

      {/* Metadata: outcome chips + paired file + notes */}
      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <label
            className="block text-[10px] uppercase font-semibold mb-1.5"
            style={{ color: "#6B7280", letterSpacing: "0.05em" }}
          >
            Outcome
          </label>
          <div className="flex flex-wrap gap-1.5">
            {(Object.keys(OUTCOME_LABELS) as FileOutcome[]).map((o) => (
              <button
                key={o}
                type="button"
                onClick={() => setOutcome((curr) => (curr === o ? null : o))}
                className="px-2.5 py-1 rounded-md text-xs font-medium transition-all"
                style={
                  outcome === o
                    ? {
                        backgroundColor: outcomeBg(o),
                        color: outcomeFg(o),
                      }
                    : {
                        backgroundColor: "rgba(255,255,255,0.04)",
                        color: "#9CA3AF",
                      }
                }
              >
                {OUTCOME_LABELS[o]}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label
            className="block text-[10px] uppercase font-semibold mb-1.5"
            style={{ color: "#6B7280", letterSpacing: "0.05em" }}
          >
            Paired with original RFP
          </label>
          <select
            value={pairedFileId ?? ""}
            onChange={(e) => setPairedFileId(e.target.value || null)}
            className="w-full px-2.5 py-1.5 rounded-md text-xs text-white border outline-none"
            style={{
              backgroundColor: "#111318",
              borderColor: "rgba(229,231,235,0.12)",
            }}
          >
            <option value="" style={{ backgroundColor: "#111318" }}>
              — None —
            </option>
            {pairCandidates.map((c) => (
              <option key={c.id} value={c.id} style={{ backgroundColor: "#111318" }}>
                {c.filename}
              </option>
            ))}
          </select>
          <p className="text-[10px] mt-1" style={{ color: "#6B7280" }}>
            Pick the RFP scope this response was written against, if it's uploaded as a Reference
            document.
          </p>
        </div>
      </div>

      <div>
        <label
          className="block text-[10px] uppercase font-semibold mb-1.5"
          style={{ color: "#6B7280", letterSpacing: "0.05em" }}
        >
          Note
        </label>
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Optional context. e.g. 'Won mostly on existing relationship', 'Lost on price', 'Came in 2nd'."
          rows={2}
          className="w-full"
        />
      </div>

      <div className="flex items-center justify-end gap-3">
        {savedAt && !isDirty && (
          <span className="text-[10px]" style={{ color: "#22c55e" }}>
            Saved.
          </span>
        )}
        <Button
          variant="secondary"
          size="sm"
          onClick={handleSave}
          disabled={!isDirty || saving}
          loading={saving}
        >
          Save details
        </Button>
      </div>
    </div>
  );
}

function outcomeBg(o: FileOutcome): string {
  switch (o) {
    case "won":
      return "rgba(34,197,94,0.18)";
    case "lost":
      return "rgba(239,68,68,0.18)";
    case "submitted":
      return "rgba(99,102,241,0.18)";
    case "draft":
      return "rgba(156,163,175,0.18)";
  }
}

function outcomeFg(o: FileOutcome): string {
  switch (o) {
    case "won":
      return "#22c55e";
    case "lost":
      return "#ef4444";
    case "submitted":
      return "#818cf8";
    case "draft":
      return "#9CA3AF";
  }
}

// ---------------------------------------------------------------------------
// DocsUploadZone — multi-file drop / click-to-pick.
// ---------------------------------------------------------------------------

function DocsUploadZone({ onAddFiles }: { onAddFiles: (files: File[]) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;
      onAddFiles(Array.from(files));
    },
    [onAddFiles],
  );

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        handleFiles(e.dataTransfer.files);
      }}
      onClick={() => inputRef.current?.click()}
      className={cn(
        "rounded-xl border-2 border-dashed px-6 py-8 flex flex-col items-center justify-center gap-2 cursor-pointer transition-all",
      )}
      style={{
        borderColor: dragOver ? "rgba(249,115,22,0.5)" : "rgba(229,231,235,0.15)",
        backgroundColor: dragOver ? "rgba(249,115,22,0.05)" : "transparent",
      }}
    >
      <input
        ref={inputRef}
        type="file"
        multiple
        accept="application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/png,image/jpeg"
        onChange={(e) => {
          handleFiles(e.target.files);
          if (inputRef.current) inputRef.current.value = "";
        }}
        className="hidden"
      />
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center"
        style={{ backgroundColor: "rgba(249,115,22,0.1)" }}
      >
        <Upload size={18} style={{ color: "#F97316" }} />
      </div>
      <p className="text-sm text-white font-medium">
        Drop PDFs, Word docs, or images — or click to browse
      </p>
      <p className="text-xs" style={{ color: "#6B7280" }}>
        Multiple files OK · max 10 MB each
      </p>
    </div>
  );
}
